import {
	BankAccountTimelineEvent,
	BankAccountTimelineLabel,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/bankAccountTimeline.types.ts';
import { toAuditEntries } from '@dealstack/frontend/shared/components/Audit/AuditLayout/auditWrites.utils.ts';
import { AuditAction, AuditTct, BankAccountStatus } from '@dealstack/shared/lib/generated';

const STATE_LABELS: Record<BankAccountStatus, BankAccountTimelineLabel> = {
	[BankAccountStatus.DRAFT]: {
		translationKey: 'bank_accounts.details.history.state.draft',
		defaultValue: 'Saved as draft',
	},
	[BankAccountStatus.PENDING_VERIFICATION]: {
		translationKey: 'bank_accounts.details.history.state.pending_verification',
		defaultValue: 'Awaiting validation',
	},
	[BankAccountStatus.PENDING_ADMIN_REVIEW]: {
		translationKey: 'bank_accounts.details.history.state.pending_admin_review',
		defaultValue: 'Submitted for review',
	},
	// The key predates the APPROVED status name; the copy behind it reads "Approved".
	[BankAccountStatus.APPROVED]: {
		translationKey: 'bank_accounts.details.history.state.verified',
		defaultValue: 'Approved',
	},
	[BankAccountStatus.REJECTED]: {
		translationKey: 'bank_accounts.details.history.state.rejected',
		defaultValue: 'Declined',
	},
	[BankAccountStatus.INACTIVE]: {
		translationKey: 'bank_accounts.details.history.state.inactive',
		defaultValue: 'Made inactive',
	},
	[BankAccountStatus.INVESTOR_OTP_REQUESTED]: {
		translationKey: 'bank_accounts.details.history.state.investor_otp_requested',
		defaultValue: 'Confirmation code sent',
	},
	[BankAccountStatus.INVESTOR_OTP_SUCCEEDED]: {
		translationKey: 'bank_accounts.details.history.state.investor_otp_succeeded',
		defaultValue: 'Confirmed by investor',
	},
	[BankAccountStatus.INVESTOR_OTP_FAILED]: {
		translationKey: 'bank_accounts.details.history.state.investor_otp_failed',
		defaultValue: 'Confirmation failed',
	},
};

const ACCOUNT_ADDED: BankAccountTimelineLabel = {
	translationKey: 'bank_accounts.details.history.event.created',
	defaultValue: 'Account added',
};
const ACCOUNT_CHANGED: BankAccountTimelineLabel = {
	translationKey: 'bank_accounts.details.history.event.updated',
	defaultValue: 'Account changed',
};
const ACCOUNT_REMOVED: BankAccountTimelineLabel = {
	translationKey: 'bank_accounts.details.history.event.removed',
	defaultValue: 'Account removed',
};
const ACCOUNT_RESTORED: BankAccountTimelineLabel = {
	translationKey: 'bank_accounts.details.history.event.restored',
	defaultValue: 'Account restored',
};

const BANK_ACCOUNT_TABLE = 'BankAccount';

const isBankAccountStatus = (value: unknown): value is BankAccountStatus =>
	typeof value === 'string' && value in STATE_LABELS;

const readStatus = (data: unknown): BankAccountStatus | undefined => {
	if (!data || typeof data !== 'object' || !('status' in data)) {
		return undefined;
	}

	return isBankAccountStatus(data.status) ? data.status : undefined;
};

const resolveLabel = (group: AuditTct[]): BankAccountTimelineLabel => {
	const accountAudit = group.find((audit) => audit.tableName === BANK_ACCOUNT_TABLE);

	if (accountAudit?.action === AuditAction.CREATE) {
		return ACCOUNT_ADDED;
	}
	if (accountAudit?.action === AuditAction.DELETE) {
		return ACCOUNT_REMOVED;
	}
	if (accountAudit?.action === AuditAction.RESTORE) {
		return ACCOUNT_RESTORED;
	}

	const status = accountAudit ? readStatus(accountAudit.newData) : undefined;

	// Where the account landed says more than "changed" — the fields that moved alongside
	// it are in the entry's own diff.
	return status && status !== readStatus(accountAudit?.oldData)
		? STATE_LABELS[status]
		: ACCOUNT_CHANGED;
};

/**
 * Turns raw audit rows into the events the timeline reads out: one entry per write, named
 * for the state it reached when the status moved and a plain change otherwise.
 */
export const toBankAccountTimelineEvents = (audits: AuditTct[]): BankAccountTimelineEvent[] =>
	toAuditEntries(audits).map((entry) => ({ ...resolveLabel(entry.audits), ...entry }));

/**
 * Which entry the timeline has open: the reviewer's pick while it is still on the list,
 * otherwise the newest. Nothing is open away from the timeline, where the main region
 * belongs to the account's own fields.
 */
export const resolveOpenEvent = (
	events: BankAccountTimelineEvent[],
	selectedEventId: string | null,
	isTimelineOpen: boolean
): BankAccountTimelineEvent | null => {
	if (!isTimelineOpen) {
		return null;
	}

	return events.find((event) => event.audit.id === selectedEventId) ?? events[0] ?? null;
};

/** "Submitted for review by Sarah Lee" — the entry's own name, attributed to whoever
 *  caused it ("System Process" for anything the platform did itself). */
export const toBankAccountEventTitle = (
	event: BankAccountTimelineEvent,
	tMipManage: (key: string, options?: { defaultValue: string }) => string
): string => {
	const label = tMipManage(event.translationKey, { defaultValue: event.defaultValue });
	const actor = event.audit.userDisplayName;

	if (!actor) {
		return label;
	}

	return `${label} ${tMipManage('bank_accounts.details.history.by', { defaultValue: 'by' })} ${actor}`;
};
